// ============================================================
//  Threat Intelligence — Daily Threat Briefing (Insights)
// ============================================================
//
// The aggregate view: what individual headlines don't reveal on their own.
// Combines a deterministic programmatic layer (IoC/CVE extraction, curated
// actor/malware dictionaries, cross-source correlation, per-entity TTP
// profiles) with an AI synthesis layer that narrates the last 24 hours, ranks
// threats and surfaces non-obvious connections. Served from
// /api/threat-intel/insights.

import React, { useCallback, useEffect, useState } from "react";
import {
  FaSyncAlt,
  FaSpinner,
  FaBrain,
  FaExternalLinkAlt,
  FaExclamationTriangle,
  FaProjectDiagram,
  FaBug,
  FaChartLine,
  FaLightbulb,
  FaClipboardCheck,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
};

const nvd = (cve) => `https://nvd.nist.gov/vuln/detail/${cve}`;

// Coverage list — shows the actual article titles (not just repeated source
// names), with the outlet as a small tag.
const RefList = ({ refs }) =>
  refs && refs.length ? (
    <ul className="ti-ins-reflist">
      {refs.map((r, i) => (
        <li key={`${r.link}-${i}`}>
          <a href={r.link} target="_blank" rel="noopener noreferrer" title={r.title}>
            <span className="ti-ins-ref-src">{r.source}</span>
            <span className="ti-ins-ref-title">{r.title}</span>
            <FaExternalLinkAlt aria-hidden />
          </a>
        </li>
      ))}
    </ul>
  ) : null;

export default function ThreatInsights() {
  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useCallback(
    (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra }),
    [token]
  );

  const load = useCallback(
    async ({ force = false } = {}) => {
      setError("");
      if (force) setRefreshing(true);
      try {
        const res = await fetch(`/api/threat-intel/insights${force ? "?refresh=1" : ""}`, {
          headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        setData(json);
      } catch (e) {
        setError(e.message || "Failed to load insights");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="card ti-feed-card">
        <p className="placeholder">
          <FaSpinner className="spin" /> Compiling your daily threat briefing…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card ti-feed-card">
        <div className="error-banner">{error}</div>
      </div>
    );
  }

  const ai = data?.ai;
  const stats = data?.stats || {};
  const correlations = data?.correlations || [];
  const briefDate = data?.generatedAt
    ? new Date(data.generatedAt).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";
  const maxTrend = Math.max(1, ...(data?.trending || []).map((t) => t.count));

  return (
    <div className="ti-ins">
      {/* Header */}
      <div className="card upload-card">
        <div className="upload-header">
          <div>
            <h2 className="section-title">
              <FaBrain style={{ marginRight: 8 }} />
              Daily Threat Briefing
            </h2>
            <p className="section-desc">
              {briefDate && <strong>{briefDate}</strong>} — security activity across{" "}
              <strong>{stats.sources?.length ?? 0}</strong> sources, prioritising the last 24 hours.{" "}
              {data?.generatedAt && (
                <span className="ti-updated">Generated {fmtRelative(data.generatedAt)}.</span>
              )}
            </p>
          </div>
          <div className="ti-actions">
            <button
              type="button"
              className="ti-refresh-btn"
              onClick={() => load({ force: true })}
              disabled={refreshing}
              title="Re-analyze the latest articles"
            >
              {refreshing ? <FaSpinner className="spin" /> : <FaSyncAlt />}
              {refreshing ? " Analyzing…" : " Re-analyze"}
            </button>
          </div>
        </div>

        {!data?.aiAvailable && (
          <div className="ti-warn">
            <FaExclamationTriangle /> No AI provider is configured — showing programmatic
            insights only. An admin can enable one in Settings → Integration for AI synthesis.
          </div>
        )}
        {data?.aiError && data?.aiAvailable && (
          <div className="ti-warn">
            <FaExclamationTriangle /> AI synthesis unavailable ({data.aiError}). Showing
            programmatic insights.
          </div>
        )}
      </div>

      {/* AI executive briefing */}
      {ai?.executiveSummary && (
        <div className="card ti-ins-summary">
          <div className="ti-ins-summary-badge">
            <FaBrain aria-hidden /> AI Daily Briefing
          </div>
          <p>{ai.executiveSummary}</p>
        </div>
      )}

      {/* Patterns + Recommended actions — most actionable, kept at the top */}
      {(ai?.emergingPatterns?.length > 0 || ai?.recommendations?.length > 0) && (
        <div className="ti-ins-grid">
          {ai?.emergingPatterns?.length > 0 && (
            <div className="card ti-ins-section">
              <h3 className="ti-ins-h3">
                <FaLightbulb aria-hidden /> Emerging Patterns
              </h3>
              <ul className="ti-ins-list">
                {ai.emergingPatterns.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          {ai?.recommendations?.length > 0 && (
            <div className="card ti-ins-section">
              <h3 className="ti-ins-h3">
                <FaClipboardCheck aria-hidden /> Recommended Actions
              </h3>
              <ul className="ti-ins-list checks">
                {ai.recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Indicators & trends — quick data snapshot, near the top */}
      <div className="ti-ins-grid">
        <div className="card ti-ins-section">
          <h3 className="ti-ins-h3">
            <FaBug aria-hidden /> Vulnerabilities (CVEs)
            {data?.iocs?.cves?.length > 0 && (
              <span className="ti-ins-h3-hint">{data.iocs.cves.length} referenced</span>
            )}
          </h3>
          <p className="ti-ins-sub-desc">
            CVEs named across the feed. Those cited by multiple outlets are typically the most active.
          </p>
          {data?.iocs?.cves?.length > 0 ? (
            <ul className="ti-ins-cvelist">
              {data.iocs.cves.slice(0, 12).map((c) => (
                <li key={c.value} className={c.sources.length >= 2 ? "hot" : ""}>
                  <a className="ti-ins-cve" href={nvd(c.value)} target="_blank" rel="noopener noreferrer">
                    <FaBug aria-hidden />
                    <span>{c.value}</span>
                  </a>
                  <span className={`ti-ins-cve-outlets ${c.sources.length >= 2 ? "multi" : ""}`}>
                    {c.sources.length} outlet{c.sources.length === 1 ? "" : "s"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ti-ins-empty">No CVEs referenced in the current set.</p>
          )}
        </div>

        <div className="card ti-ins-section">
          <h3 className="ti-ins-h3">
            <FaChartLine aria-hidden /> Trending Techniques
          </h3>
          <p className="ti-ins-sub-desc">Most-mentioned attack techniques across the corpus.</p>
          {data?.trending?.length > 0 ? (
            <div className="ti-ins-trend">
              {data.trending.slice(0, 12).map((t) => (
                <div key={t.label} className="ti-ins-trend-row">
                  <span className="ti-ins-trend-label">{t.label}</span>
                  <span className="ti-ins-trend-bar">
                    <span style={{ width: `${(t.count / maxTrend) * 100}%` }} />
                  </span>
                  <span className="ti-ins-trend-n">{t.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="ti-ins-empty">No technique signals detected.</p>
          )}
        </div>
      </div>

      {/* Key threats (AI) */}
      {ai?.keyThreats?.length > 0 && (
        <div className="card ti-ins-section">
          <h3 className="ti-ins-h3">
            <FaExclamationTriangle aria-hidden /> Key Threats
          </h3>
          <div className="ti-ins-threats">
            {ai.keyThreats.map((t, i) => (
              <div key={i} className={`ti-ins-threat sev-${t.severity}`}>
                <div className="ti-ins-threat-head">
                  <span className={`ti-ins-sev sev-${t.severity}`}>{t.severity}</span>
                  <h4>{t.title}</h4>
                </div>
                {t.summary && <p>{t.summary}</p>}
                <RefList refs={t.refs} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connect the dots — AI correlations + cross-source CVE/actor/malware signals */}
      {(ai?.correlations?.length > 0 || correlations.length > 0) && (
        <div className="card ti-ins-section">
          <h3 className="ti-ins-h3">
            <FaProjectDiagram aria-hidden /> Connect the Dots
          </h3>

          {ai?.correlations?.map((c, i) => (
            <div key={`ai-${i}`} className="ti-ins-corr ai">
              <p>{c.insight}</p>
              <RefList refs={c.refs} />
            </div>
          ))}

          {correlations.length > 0 && (
            <>
              <div className="ti-ins-subhead">Cross-source signals</div>
              {correlations.map((c, i) => (
                <div key={`p-${i}`} className="ti-ins-corr">
                  <div className="ti-ins-corr-head">
                    <span className={`ti-ins-corr-type type-${c.type}`}>{c.type}</span>
                    <span className="ti-ins-corr-label">{c.label}</span>
                    <span className="ti-ins-corr-meta">
                      {c.sources.length} outlet{c.sources.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <RefList refs={c.refs} />
                </div>
              ))}
            </>
          )}
        </div>
      )}

    </div>
  );
}
